import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  WireframeLayout,
  WireframeHeader,
  WireframeContent,
  WireframeTabs,
  WireframeCard,
  WireframeButton,
  WireframeTable,
  StatusIndicator,
  CrisisBadge,
  StatCard,
} from './WireframeLayout';
import { LoadingSpinner, useToast, EmptyState } from './WireframeUI';
import { TutorialStartButton } from './WireframeTutorial';
import { Download, PieChart, AlertTriangle, Clock, CheckCircle, FileText, Sparkles, ChevronDown, Shield, Droplets, Users, Home, Heart, Utensils } from 'lucide-react';
import { cn } from '../../lib/utils';
import { wireframeMockApi } from '../../services/wireframeMockApi';
import {
  getSituationData,
  getAllNRCCountries,
  getRegionsForCountry,
  getCountriesWithData,
  SituationData,
} from '../../data/situationData';
import { nrcLocations } from '../../data/nrcLocations';

// FilterDropdown component (reused from WireframeOverview)
function FilterDropdown({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={dropdownRef}>
      <div
        onClick={() => options.length > 0 && setIsOpen(!isOpen)}
        className="bg-white border border-gray-200 px-3 py-1.5 min-w-[120px] cursor-pointer hover:border-gray-300"
      >
        <div className="text-[9px] text-gray-500 uppercase tracking-wide">{label}</div>
        <div className="text-[12px] font-semibold flex items-center justify-between gap-2">
          {value}{' '}
          <ChevronDown className={cn("w-3 h-3 text-gray-400 transition-transform", isOpen && "rotate-180")} />
        </div>
      </div>

      {isOpen && options.length > 0 && (
        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 shadow-lg z-50 min-w-[160px] max-h-[300px] overflow-y-auto">
          {options.map((option) => (
            <div
              key={option}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
              className={cn(
                "px-3 py-2 text-sm cursor-pointer hover:bg-gray-50",
                value === option && "bg-gray-100 font-medium"
              )}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Severity Badge component
function SeverityBadge({ severity }: { severity: 'critical' | 'high' | 'medium' }) {
  const styles = {
    critical: 'bg-red-100 text-red-700 border-red-200',
    high: 'bg-amber-100 text-amber-700 border-amber-200',
    medium: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  };
  return (
    <span className={cn('text-[10px] font-semibold uppercase px-2 py-0.5 border', styles[severity])}>
      {severity}
    </span>
  );
}

// Sector icon mapping
const sectorIcons: Record<string, React.ReactNode> = {
  'Shelter': <Home className="w-4 h-4" />,
  'WASH': <Droplets className="w-4 h-4" />,
  'Protection': <Shield className="w-4 h-4" />,
  'Health': <Heart className="w-4 h-4" />,
  'Food Security': <Utensils className="w-4 h-4" />,
};

const situationReports = [
  {
    title: 'Cholera Outbreak Analysis',
    region: 'Somali Region',
    severity: 'critical' as const,
    description: 'Detailed epidemiological analysis with 72-hour projection. Recommends immediate WASH intervention.',
  },
  {
    title: 'Flood Risk Assessment',
    region: 'Oromia Region',
    severity: 'high' as const,
    description: 'Weather pattern analysis indicates elevated flooding risk. Pre-positioning recommended.',
  },
  {
    title: 'Drought Monitoring Update',
    region: 'Afar Region',
    severity: 'medium' as const,
    description: 'Long-term drought indicators show gradual deterioration. Early action window available.',
  },
];

const scenarios = [
  {
    name: 'Best Case',
    subtitle: 'Rapid containment scenario',
    likelihood: '25%',
    color: 'green',
    description: 'Early intervention with full WASH coverage achieves containment within 2 weeks. Estimated affected: 2,400 people.',
  },
  {
    name: 'Most Likely',
    subtitle: 'Moderate spread scenario',
    likelihood: '55%',
    color: 'blue',
    highlight: true,
    description: 'Partial containment with ongoing transmission in 3 woredas. Peak in 3-4 weeks. Estimated affected: 8,500 people.',
  },
  {
    name: 'Worst Case',
    subtitle: 'Regional spread scenario',
    likelihood: '20%',
    color: 'red',
    description: 'Widespread transmission across 8+ woredas with health system strain. Estimated affected: 24,000+ people.',
  },
];

const insights = [
  {
    type: 'critical',
    icon: <AlertTriangle className="w-4 h-4" />,
    label: 'Critical Pattern',
    title: 'Water Source Clustering',
    description: '87% of cases within 500m of 3 water points. Immediate testing and treatment recommended.',
  },
  {
    type: 'warning',
    icon: <Clock className="w-4 h-4" />,
    label: 'Time Sensitive',
    title: 'Peak Timing Prediction',
    description: 'Historical patterns suggest peak transmission 14-21 days from outbreak start. Current: Day 8.',
  },
  {
    type: 'success',
    icon: <CheckCircle className="w-4 h-4" />,
    label: 'Opportunity',
    title: 'Resource Optimization',
    description: 'ORS supplies in Dire Dawa can reach affected areas within 6 hours. Pre-positioning available.',
  },
];

const dataQuality = [
  { source: 'MOH PHEM Data', completeness: 94, timeliness: 'Real-time', confidence: 'High', lastUpdate: '3 min ago' },
  { source: 'WHO EWARN', completeness: 88, timeliness: 'Hourly', confidence: 'High', lastUpdate: '1h ago' },
  { source: 'Field Reports', completeness: 76, timeliness: 'Daily', confidence: 'Medium', lastUpdate: '4h ago' },
  { source: 'Satellite Imagery', completeness: 92, timeliness: '6h cycle', confidence: 'High', lastUpdate: '2h ago' },
];

export function WireframeAnalysis() {
  const [searchParams] = useSearchParams();
  const isEmbedded = searchParams.get('embedded') === 'true';

  // Notify parent window of current page for tooltip coordination
  useEffect(() => {
    if (isEmbedded && window.parent !== window) {
      window.parent.postMessage({ type: 'CLEAR_DEMO_PAGE', page: 'analysis' }, '*');
    }
  }, [isEmbedded]);

  const [activeTab, setActiveTab] = useState(0);
  const [exporting, setExporting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('Sudan');
  const [selectedRegion, setSelectedRegion] = useState('All Regions');
  const [selectedDate, setSelectedDate] = useState('Feb 2026');
  const { addToast } = useToast();

  // Date options for the dropdown
  const dateOptions = [
    'Feb 2026',
    'Jan 2026',
    'Dec 2025',
    'Nov 2025',
    'Oct 2025',
    'Last 7 days',
    'Last 30 days',
    'Last 90 days',
  ];

  const countriesWithData = getCountriesWithData();
  const allCountries = getAllNRCCountries();
  const regions = getRegionsForCountry(selectedCountry);
  const situationData = getSituationData(selectedCountry);
  const hasFullData = countriesWithData.includes(selectedCountry);
  const nrcLocation = nrcLocations.find(loc => loc.country === selectedCountry);

  const tabs = [
    { label: 'Situation Reports', active: activeTab === 0 },
    { label: 'Scenario Planning', active: activeTab === 1 },
    { label: 'Impact Assessment', active: activeTab === 2 },
    { label: 'AI Insights', active: activeTab === 3 },
  ];

  const handleCountryChange = (country: string) => {
    setSelectedCountry(country);
    setSelectedRegion('All Regions');
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const blob = await wireframeMockApi.exportToPdf('analysis', { reports: situationReports, scenarios });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `CLEAR_Analysis_Report_${selectedCountry}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      addToast({ type: 'success', title: 'Report exported', message: 'Analysis report has been downloaded.' });
    } catch (error) {
      addToast({ type: 'error', title: 'Export failed', message: 'Unable to generate report.' });
    } finally {
      setExporting(false);
    }
  };

  const handleGenerate = async () => {
    setGenerating(true);
    addToast({ type: 'info', title: 'Generating analysis', message: 'AI is analyzing the latest data...' });

    await new Promise(resolve => setTimeout(resolve, 2500));

    setGenerating(false);
    addToast({ type: 'success', title: 'Analysis complete', message: 'New situation report has been generated.' });
  };

  return (
    <WireframeLayout embedded={isEmbedded}>
      {!isEmbedded && <WireframeHeader
        title="AI-Powered Analysis"
        breadcrumbs={[
          { label: 'CLEAR', href: '/wireframes' },
          { label: 'Analysis' }
        ]}
        actions={
          <>
            <FilterDropdown
              label="Country"
              value={selectedCountry}
              options={allCountries}
              onChange={handleCountryChange}
            />
            <FilterDropdown
              label="Region"
              value={selectedRegion}
              options={regions}
              onChange={setSelectedRegion}
            />
            <FilterDropdown
              label="Date"
              value={selectedDate}
              options={dateOptions}
              onChange={setSelectedDate}
            />
            <TutorialStartButton tutorialId="analysis" variant="icon" />
            <div data-tutorial="export">
              <WireframeButton
                icon={exporting ? <LoadingSpinner size="sm" /> : <Download className="w-4 h-4" />}
                onClick={handleExport}
                className={exporting ? 'opacity-75' : ''}
              >
                {exporting ? 'Exporting...' : 'Export Report'}
              </WireframeButton>
            </div>
            <div data-tutorial="generate">
              <WireframeButton
                variant="primary"
                icon={generating ? <LoadingSpinner size="sm" className="text-white" /> : <Sparkles className="w-4 h-4" />}
                onClick={handleGenerate}
                className={generating ? 'opacity-75' : ''}
              >
                {generating ? 'Generating...' : 'Generate Analysis'}
              </WireframeButton>
            </div>
          </>
        }
      />}

      <WireframeContent>
        <div data-tutorial="analysis-type">
          <WireframeTabs tabs={tabs} onTabClick={setActiveTab} />
        </div>

        {/* Executive Summary Banner */}
        {hasFullData && situationData ? (
          <div className="mb-6 border border-accent-500 bg-gradient-to-r from-blue-50 to-sky-50 p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-accent-500 flex items-center justify-center flex-shrink-0">
                <PieChart className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <div className="text-[11px] text-accent-500 font-semibold uppercase tracking-wide">
                    Latest AI Analysis
                  </div>
                  <SeverityBadge severity={situationData.executiveSummary.severity} />
                  <span className={cn(
                    'text-[9px] font-bold uppercase px-1.5 py-0.5',
                    situationData.executiveSummary.securityLevel === 'red' && 'bg-red-600 text-white',
                    situationData.executiveSummary.securityLevel === 'amber' && 'bg-amber-500 text-white',
                    situationData.executiveSummary.securityLevel === 'green' && 'bg-green-600 text-white',
                  )}>
                    Security: {situationData.executiveSummary.securityLevel.toUpperCase()}
                  </span>
                </div>
                <div className="font-semibold text-gray-900 mb-2">
                  {situationData.executiveSummary.title} - {selectedCountry}
                </div>
                <div className="flex flex-wrap gap-4 mb-3">
                  <div className="text-center">
                    <div className="text-xl font-bold text-red-600">{situationData.executiveSummary.displaced.toLocaleString()}</div>
                    <div className="text-[10px] text-gray-500 uppercase">Displaced</div>
                  </div>
                  <div className="text-center">
                    <div className="text-xl font-bold text-gray-900">{situationData.executiveSummary.households.toLocaleString()}</div>
                    <div className="text-[10px] text-gray-500 uppercase">Households</div>
                  </div>
                  <div className="text-center">
                    <div className={cn(
                      'text-xl font-bold',
                      situationData.executiveSummary.trendDirection === 'up' ? 'text-red-600' : 'text-green-600'
                    )}>
                      {situationData.executiveSummary.trend}
                    </div>
                    <div className="text-[10px] text-gray-500 uppercase">Trend</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1.5 mb-3">
                  {situationData.executiveSummary.sectors.map(sector => (
                    <span key={sector} className="text-[10px] px-2 py-0.5 bg-white border border-gray-200 text-gray-600">
                      {sector}
                    </span>
                  ))}
                </div>
                <div className="flex gap-2">
                  <WireframeButton variant="primary" size="sm" icon={<FileText className="w-3 h-3" />}>View Full Report</WireframeButton>
                  <WireframeButton size="sm" icon={<Download className="w-3 h-3" />} onClick={handleExport}>Download PDF</WireframeButton>
                </div>
              </div>
              <div className="text-right text-xs text-gray-500 flex-shrink-0" data-tutorial="confidence">
                <div>Updated {situationData.lastUpdated}</div>
                <div className="mt-1">Confidence: 94%</div>
                <div className="mt-2 text-[9px]">
                  <span className="text-accent-500">Data Overview</span> | Response Pitch | Sources
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="mb-6 border border-gray-300 bg-gray-50 p-5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-gray-400 flex items-center justify-center flex-shrink-0">
                <PieChart className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <div className="text-[11px] text-gray-500 font-semibold uppercase tracking-wide mb-1">
                  NRC Country Profile
                </div>
                <div className="font-semibold text-gray-900 mb-2">
                  {selectedCountry} - {nrcLocation?.region || 'Overview'}
                </div>
                <p className="text-[13px] text-gray-600 leading-relaxed mb-3">
                  {nrcLocation?.description || `NRC operates in ${selectedCountry} providing humanitarian assistance.`}
                  {' '}Detailed crisis analysis data is available for Sudan and Ethiopia.
                </p>
                <div className="flex gap-2">
                  <WireframeButton size="sm" onClick={() => handleCountryChange('Sudan')}>View Sudan Analysis</WireframeButton>
                  <WireframeButton size="sm" onClick={() => handleCountryChange('Ethiopia')}>View Ethiopia Analysis</WireframeButton>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 0 && (
          /* Situation Reports Tab */
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Current Situation Card */}
            {hasFullData && situationData && (
              <WireframeCard
                title="Current Situation"
                subtitle={`${selectedCountry} - ${situationData.region}`}
                headerAction={
                  <span className={cn(
                    'text-[9px] font-bold uppercase px-2 py-0.5',
                    situationData.executiveSummary.securityLevel === 'red' && 'bg-red-100 text-red-700',
                    situationData.executiveSummary.securityLevel === 'amber' && 'bg-amber-100 text-amber-700',
                    situationData.executiveSummary.securityLevel === 'green' && 'bg-green-100 text-green-700',
                  )}>
                    {situationData.executiveSummary.crisisType}
                  </span>
                }
              >
                <ul className="space-y-2">
                  {situationData.currentSituation.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-[13px] text-gray-600">
                      <span className="w-1.5 h-1.5 bg-red-600 mt-1.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </WireframeCard>
            )}

            {/* Population Impact Card */}
            {hasFullData && situationData && (
              <WireframeCard
                title="Population Impact"
                subtitle="Affected demographics"
              >
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="text-center p-3 bg-red-50 border-l-2 border-red-600">
                    <div className="text-2xl font-bold text-red-600">{situationData.populationImpact.households.toLocaleString()}</div>
                    <div className="text-[10px] text-gray-500 uppercase">Households Affected</div>
                  </div>
                  <div className="text-center p-3 bg-amber-50 border-l-2 border-amber-500">
                    <div className="text-2xl font-bold text-amber-600">{situationData.populationImpact.totalDisplaced.toLocaleString()}</div>
                    <div className="text-[10px] text-gray-500 uppercase">Total Displaced</div>
                  </div>
                </div>
                <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 mb-2">Vulnerable Groups</div>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="text-center p-2 bg-gray-50">
                    <div className="text-lg font-bold text-gray-900">{situationData.populationImpact.children.toLocaleString()}</div>
                    <div className="text-[9px] text-gray-500">Children</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50">
                    <div className="text-lg font-bold text-gray-900">{situationData.populationImpact.women.toLocaleString()}</div>
                    <div className="text-[9px] text-gray-500">Women</div>
                  </div>
                  <div className="text-center p-2 bg-gray-50">
                    <div className="text-lg font-bold text-gray-900">{situationData.populationImpact.elderly.toLocaleString()}</div>
                    <div className="text-[9px] text-gray-500">Elderly</div>
                  </div>
                </div>
                <div className="space-y-2 text-[12px]">
                  <div className="flex items-center gap-2 text-amber-700">
                    <AlertTriangle className="w-3 h-3" />
                    {situationData.populationImpact.infrastructureStress}
                  </div>
                  <div className="flex items-center gap-2 text-red-700">
                    <Users className="w-3 h-3" />
                    {situationData.populationImpact.hostCapacity}
                  </div>
                </div>
              </WireframeCard>
            )}

            {/* Situation Reports */}
            <WireframeCard
              title="Situation Reports"
              subtitle="AI-generated assessments"
              headerAction={<WireframeButton size="sm">View All</WireframeButton>}
              noPadding
            >
              <div className="relative pl-6">
                <div className="absolute left-[7px] top-0 bottom-0 w-0.5 bg-gray-200" />
                {situationReports.map((report, index) => (
                  <div key={report.title} className="relative pb-5 last:pb-0">
                    <div
                      className={cn(
                        'absolute left-[-20px] top-1 w-2.5 h-2.5 border-2 border-gray-200 bg-white',
                        report.severity === 'critical' && 'bg-red-600 border-red-600',
                        report.severity === 'high' && 'bg-amber-500 border-amber-500',
                        report.severity === 'medium' && 'bg-amber-600 border-amber-600'
                      )}
                    />
                    <div className="pt-5 pr-5">
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold text-sm text-gray-900">{report.title}</div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {report.region} • {report.severity.charAt(0).toUpperCase() + report.severity.slice(1)} severity
                          </div>
                        </div>
                        <CrisisBadge severity={report.severity} />
                      </div>
                      <p className="text-xs text-gray-600 mt-2 leading-relaxed">{report.description}</p>
                      <button
                        onClick={() => addToast({ type: 'info', title: 'Opening report', message: `Loading ${report.title}...` })}
                        className="text-xs text-accent-500 font-medium mt-2 inline-block hover:text-accent-600"
                      >
                        Read full report →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </WireframeCard>

            {/* Scenario Planning */}
            <WireframeCard
              title="Scenario Planning"
              subtitle="Projection models"
              headerAction={<WireframeButton size="sm">+ New Scenario</WireframeButton>}
            >
              <div className="space-y-3">
                {scenarios.map((scenario) => (
                  <div
                    key={scenario.name}
                    className={cn(
                      'p-4 bg-gray-50',
                      scenario.highlight && 'border-l-[3px] border-accent-500'
                    )}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="font-semibold text-gray-900">{scenario.name}</div>
                        <div className="text-xs text-gray-500">{scenario.subtitle}</div>
                      </div>
                      <span
                        className={cn(
                          'text-xs px-2 py-0.5',
                          scenario.color === 'green' && 'bg-green-100 text-green-700',
                          scenario.color === 'blue' && 'bg-blue-100 text-accent-500',
                          scenario.color === 'red' && 'bg-red-100 text-red-600'
                        )}
                      >
                        {scenario.likelihood} likely
                      </span>
                    </div>
                    <p className="text-[13px] text-gray-600 leading-relaxed">{scenario.description}</p>
                  </div>
                ))}
              </div>
            </WireframeCard>
          </div>
        )}

        {activeTab === 1 && (
          /* Scenario Planning Tab - Extended View */
          <WireframeCard title="Scenario Comparison" subtitle="Compare projected outcomes">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {scenarios.map((scenario) => (
                <div
                  key={scenario.name}
                  className={cn(
                    'p-5 border',
                    scenario.color === 'green' && 'border-green-200 bg-green-50',
                    scenario.color === 'blue' && 'border-blue-200 bg-blue-50',
                    scenario.color === 'red' && 'border-red-200 bg-red-50'
                  )}
                >
                  <div className={cn(
                    'text-2xl font-bold mb-2',
                    scenario.color === 'green' && 'text-green-700',
                    scenario.color === 'blue' && 'text-blue-700',
                    scenario.color === 'red' && 'text-red-700'
                  )}>
                    {scenario.likelihood}
                  </div>
                  <div className="font-semibold text-gray-900">{scenario.name}</div>
                  <div className="text-xs text-gray-500 mb-3">{scenario.subtitle}</div>
                  <p className="text-sm text-gray-600 leading-relaxed">{scenario.description}</p>
                </div>
              ))}
            </div>
          </WireframeCard>
        )}

        {activeTab === 2 && (
          /* Impact Assessment Tab */
          <>
            {hasFullData && situationData ? (
              <div className="space-y-6">
                {/* Impact Assessment Grid */}
                <WireframeCard
                  title="Sector Impact Assessment"
                  subtitle="Humanitarian needs by sector"
                  noPadding
                >
                  <WireframeTable
                    columns={[
                      { key: 'sector', label: 'Sector' },
                      { key: 'severity', label: 'Severity' },
                      { key: 'number', label: 'Affected' },
                      { key: 'description', label: 'Details' },
                    ]}
                    data={situationData.impactAssessment.map((item) => ({
                      sector: (
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            'w-8 h-8 flex items-center justify-center',
                            item.severity === 'critical' && 'bg-red-100 text-red-600',
                            item.severity === 'high' && 'bg-amber-100 text-amber-600',
                            item.severity === 'medium' && 'bg-yellow-100 text-yellow-600',
                          )}>
                            {sectorIcons[item.sector] || <AlertTriangle className="w-4 h-4" />}
                          </span>
                          <strong>{item.sector}</strong>
                        </div>
                      ),
                      severity: <SeverityBadge severity={item.severity} />,
                      number: (
                        <span className={cn(
                          'font-bold',
                          item.severity === 'critical' && 'text-red-600',
                          item.severity === 'high' && 'text-amber-600',
                          item.severity === 'medium' && 'text-yellow-600',
                        )}>
                          {item.number.toLocaleString()} {item.unit}
                        </span>
                      ),
                      description: item.description,
                    }))}
                  />
                </WireframeCard>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Environmental Factors */}
                  <WireframeCard
                    title="Environmental Factors"
                    subtitle="Climate and environmental risks"
                  >
                    <div className="space-y-3">
                      <div className="p-3 bg-amber-50 border-l-2 border-amber-500">
                        <div className="text-[10px] font-bold uppercase text-amber-700 mb-1">Flood Risk</div>
                        <div className="text-[13px] text-gray-700">{situationData.environmentalFactors.floodRisk}</div>
                      </div>
                      <div className="p-3 bg-blue-50 border-l-2 border-blue-500">
                        <div className="text-[10px] font-bold uppercase text-blue-700 mb-1">Rainfall Forecast</div>
                        <div className="text-[13px] text-gray-700">{situationData.environmentalFactors.rainfallForecast}</div>
                      </div>
                      <div className="p-3 bg-red-50 border-l-2 border-red-500">
                        <div className="text-[10px] font-bold uppercase text-red-700 mb-1">Secondary Risk</div>
                        <div className="text-[13px] text-gray-700">{situationData.environmentalFactors.secondaryRisk}</div>
                      </div>
                    </div>
                  </WireframeCard>

                  {/* Protection Concerns */}
                  <WireframeCard
                    title="Protection Concerns"
                    subtitle="Identified vulnerabilities"
                  >
                    <ul className="space-y-2">
                      {situationData.protectionConcerns.map((concern, idx) => (
                        <li key={idx} className="flex items-start gap-2 text-[13px] text-gray-600 p-2 bg-purple-50 border-l-2 border-purple-500">
                          <Shield className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" />
                          {concern}
                        </li>
                      ))}
                    </ul>
                  </WireframeCard>
                </div>

                {/* Secondary Effects */}
                <WireframeCard
                  title="Secondary Effects"
                  subtitle="Anticipated cascading impacts"
                >
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {situationData.secondaryEffects.map((effect, idx) => (
                      <div key={idx} className="p-3 bg-gray-50 border border-gray-200">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                          <span className="text-[13px] text-gray-700">{effect}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </WireframeCard>
              </div>
            ) : (
              <EmptyState
                variant="no-data"
                title="No impact data available"
                message={`Detailed impact assessment data is available for Sudan and Ethiopia. Select one of these countries to view comprehensive analysis.`}
                action={{ label: 'View Sudan Data', onClick: () => handleCountryChange('Sudan') }}
              />
            )}
          </>
        )}

        {activeTab === 3 && (
          /* AI Insights Tab */
          <>
            <WireframeCard
              title="AI-Generated Insights"
              subtitle="Pattern detection and recommendations"
              headerAction={
                <div className="flex gap-2">
                  <button className="px-3 py-1.5 text-xs font-medium border border-gray-200 bg-white hover:bg-gray-50">
                    Epidemiological
                  </button>
                  <button className="px-3 py-1.5 text-xs font-medium border border-blue-200 bg-blue-50 text-accent-500">
                    All Types
                  </button>
                  <button className="px-3 py-1.5 text-xs font-medium border border-gray-200 bg-white hover:bg-gray-50">
                    Logistics
                  </button>
                </div>
              }
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {insights.map((insight) => (
                  <div
                    key={insight.title}
                    className={cn(
                      'p-4 bg-gray-50 border-l-[3px]',
                      insight.type === 'critical' && 'border-red-600',
                      insight.type === 'warning' && 'border-amber-500',
                      insight.type === 'success' && 'border-green-600'
                    )}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className={cn(
                          insight.type === 'critical' && 'text-red-600',
                          insight.type === 'warning' && 'text-amber-500',
                          insight.type === 'success' && 'text-green-600'
                        )}
                      >
                        {insight.icon}
                      </span>
                      <span
                        className={cn(
                          'text-[11px] font-semibold uppercase',
                          insight.type === 'critical' && 'text-red-600',
                          insight.type === 'warning' && 'text-amber-500',
                          insight.type === 'success' && 'text-green-600'
                        )}
                      >
                        {insight.label}
                      </span>
                    </div>
                    <div className="font-semibold text-gray-900 mb-2">{insight.title}</div>
                    <p className="text-[13px] text-gray-600 leading-relaxed">{insight.description}</p>
                  </div>
                ))}
              </div>
            </WireframeCard>

            {/* Data Quality Section */}
            <div data-tutorial="data-sources">
            <WireframeCard
              title="Analysis Data Quality"
              subtitle="Source reliability metrics"
              className="mt-6"
              noPadding
            >
              <WireframeTable
                columns={[
                  { key: 'source', label: 'Data Source' },
                  { key: 'completeness', label: 'Completeness' },
                  { key: 'timeliness', label: 'Timeliness' },
                  { key: 'confidence', label: 'Confidence' },
                  { key: 'lastUpdate', label: 'Last Update' },
                ]}
                data={dataQuality.map((item) => ({
                  source: <strong>{item.source}</strong>,
                  completeness: (
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1 bg-gray-200 max-w-[100px]">
                        <div
                          className={cn(
                            'h-full',
                            item.completeness >= 90 ? 'bg-green-600' :
                            item.completeness >= 80 ? 'bg-green-600' : 'bg-amber-600'
                          )}
                          style={{ width: `${item.completeness}%` }}
                        />
                      </div>
                      <span className="text-xs">{item.completeness}%</span>
                    </div>
                  ),
                  timeliness: (
                    <StatusIndicator
                      status={item.timeliness === 'Real-time' ? 'online' : item.timeliness === 'Daily' ? 'delayed' : 'online'}
                      label={item.timeliness}
                    />
                  ),
                  confidence: (
                    <span
                      className={cn(
                        'font-medium',
                        item.confidence === 'High' ? 'text-green-600' : 'text-amber-600'
                      )}
                    >
                      {item.confidence}
                    </span>
                  ),
                  lastUpdate: item.lastUpdate,
                }))}
              />
            </WireframeCard>
            </div>
          </>
        )}
      </WireframeContent>
    </WireframeLayout>
  );
}

// Historical Event Component
function HistoricalEvent({ title, date, outcome, lessons }: { title: string; date: string; outcome: string; lessons: string }) {
  return (
    <div className="p-4 bg-gray-50 border border-gray-200">
      <div className="flex justify-between items-start mb-2">
        <div className="font-semibold text-gray-900">{title}</div>
        <span className="text-xs text-gray-500">{date}</span>
      </div>
      <div className="grid grid-cols-2 gap-4 mt-3">
        <div>
          <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Outcome</div>
          <div className="text-sm text-gray-700">{outcome}</div>
        </div>
        <div>
          <div className="text-[10px] font-semibold text-gray-500 uppercase mb-1">Lessons Learned</div>
          <div className="text-sm text-gray-700">{lessons}</div>
        </div>
      </div>
    </div>
  );
}

export default WireframeAnalysis;
